<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { Link, router, useForm, usePage } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import { useBreakpoint } from '@/composables/useBreakpoint'
import AppLayout from '@/Layouts/AppLayout.vue'
import PageShell from '@/Components/UI/PageShell.vue'
import UiButton from '@/Components/UI/UiButton.vue'
import UiInput from '@/Components/UI/UiInput.vue'
import UiTextarea from '@/Components/UI/UiTextarea.vue'
import UiSegmented from '@/Components/UI/UiSegmented.vue'
import StickyActionBar from '@/Components/UI/StickyActionBar.vue'
import TweakControls from '@/Components/UI/TweakControls.vue'
import SettingsTabs from '@/Components/UI/SettingsTabs.vue'
import SignaturePadModal from '@/Components/UI/SignaturePadModal.vue'

defineOptions({ layout: AppLayout })
const { t } = useI18n()

const props = defineProps({
  setting:    Object,
  currencies: Array,
  display_currency_options: { type: Array, default: () => [] },
  locales:    Array,
  // Controller hands back AppSetting#errors.as_json — a hash keyed by field
  // ({ two_wheeler_clauses: ["must be an array"] }). Flatten to a string[] so
  // the banner template stays simple.
  errors:     { type: [Array, Object], default: () => ({}) },
})

const errorMessages = computed(() => {
  const e = props.errors
  if (!e) return []
  if (Array.isArray(e)) return e
  return Object.values(e).flat()
})

const form = useForm({
  app_setting: {
    // currency_code is intentionally omitted — locked at the controller
    // level (canonical THB) so no point sending it back.
    locale:             props.setting.locale,
    language:           props.setting.language,
    business_name:      props.setting.business_name,
    agreement_subtitle: props.setting.agreement_subtitle,
    business_footer:    props.setting.business_footer,
    support_email:        props.setting.support_email ?? '',
    display_currencies:   [...(props.setting.display_currencies ?? ['THB'])],
    fx_margin_pct:        props.setting.fx_margin_pct ?? 1.0,
    two_wheeler_clauses:  [...(props.setting.two_wheeler_clauses  ?? [])],
    four_wheeler_clauses: [...(props.setting.four_wheeler_clauses ?? [])],
    driver_fee_cents:     props.setting.driver_fee_cents ?? 0,
    business_logo:      null
    // default_signature is persisted directly from SignaturePadModal via
    // /settings/signature (see onSignatureSave). Not part of this form.
  }
})

function toggleDisplayCurrency(code) {
  const list = form.app_setting.display_currencies
  // Canonical (THB) is always in the list — operator can't remove it.
  if (code === props.setting.currency_code) return
  const idx = list.indexOf(code)
  if (idx >= 0) list.splice(idx, 1)
  else list.push(code)
}

// Live FX rates — surfaces what's cached for the operator + lets them
// trigger an on-demand refresh without waiting for the nightly cron.
const fxRates      = ref([...(props.setting.fx_rates ?? [])])
const fxRefreshing = ref(false)
const fxFlash      = ref('')
const fxFlashOk    = ref(false)

const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content || ''

async function refreshFxRates() {
  if (fxRefreshing.value) return
  fxRefreshing.value = true
  fxFlash.value = ''
  try {
    const res = await fetch('/settings/refresh_fx_rates', {
      method: 'POST',
      headers: { 'X-CSRF-Token': csrfToken(), 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
    })
    const body = await res.json()
    if (body.ok) {
      fxRates.value = body.rates
      fxFlashOk.value = true
      fxFlash.value = t('settings.fx_refresh_ok', { updated: body.updated, skipped: body.skipped })
    } else {
      fxFlashOk.value = false
      fxFlash.value = body.error || t('settings.fx_refresh_failed')
    }
  } catch (e) {
    fxFlashOk.value = false
    fxFlash.value = t('settings.fx_refresh_failed')
  } finally {
    fxRefreshing.value = false
  }
}

function formatDateTime(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString()
}

const clauseClass = ref('two_wheeler')
const activeClauses = computed({
  get: () => clauseClass.value === 'two_wheeler'
    ? form.app_setting.two_wheeler_clauses
    : form.app_setting.four_wheeler_clauses,
  set: (v) => {
    if (clauseClass.value === 'two_wheeler') form.app_setting.two_wheeler_clauses = v
    else form.app_setting.four_wheeler_clauses = v
  }
})

function addClause() {
  const next = [...activeClauses.value]
  const nextNum = (next[next.length - 1]?.num ?? 0) + 1
  next.push({ num: nextNum, text: '', bold: false })
  activeClauses.value = next
}

function removeClause(idx) {
  const next = [...activeClauses.value]
  next.splice(idx, 1)
  activeClauses.value = next
}

function moveClause(idx, delta) {
  const next = [...activeClauses.value]
  const target = idx + delta
  if (target < 0 || target >= next.length) return
  ;[next[idx], next[target]] = [next[target], next[idx]]
  activeClauses.value = next
}

const signatureModalOpen = ref(false)
const signaturePreview = ref(props.setting.signature_url || null)
const signatureSaving = ref(false)

// Persist the signature in a single round-trip so the operator doesn't
// have to remember to also click "Save settings". The dedicated endpoint
// hits /settings/signature with multipart and returns the new URL.
async function persistSignature(formData) {
  signatureSaving.value = true
  try {
    const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
    const res = await fetch('/settings/signature', {
      method: 'PATCH',
      credentials: 'same-origin',
      headers: token ? { 'X-CSRF-Token': token, 'Accept': 'application/json' } : { 'Accept': 'application/json' },
      body: formData,
    })
    if (!res.ok) throw new Error('Save failed')
    const { signature_url } = await res.json()
    signaturePreview.value = signature_url
  } catch (e) {
    console.error('[signature] save failed', e)
    alert('Could not save signature — please try again.')
  } finally {
    signatureSaving.value = false
  }
}

const onSignatureSave = async ({ dataUrl, blob }) => {
  // Optimistic preview so the user sees instant feedback while the upload finishes.
  signaturePreview.value = dataUrl
  signatureModalOpen.value = false
  const fd = new FormData()
  fd.append('default_signature', new File([blob], 'signature.png', { type: 'image/png' }))
  await persistSignature(fd)
}
const removeSignature = async () => {
  signaturePreview.value = null
  const fd = new FormData()
  fd.append('_remove', '1')
  await persistSignature(fd)
}

const logoFile = ref(null)
const logoPreview = ref(props.setting.logo_url)

const { isMobile } = useBreakpoint()

const page = usePage()
const activeTab = computed(() => {
  const match = page.url.match(/[?&]tab=([^&]+)/)
  const tab = match ? match[1] : 'localization'
  return ['localization', 'agreement', 'appearance'].includes(tab) ? tab : 'localization'
})

const redirectIfBlockedTab = () => {
  if (!isMobile.value) return
  const url = new URL(page.url, window.location.origin)
  if (url.searchParams.get('tab') === 'appearance') {
    url.searchParams.set('tab', 'localization')
    router.visit(url.pathname + url.search, { replace: true, preserveScroll: true })
  }
}

onMounted(redirectIfBlockedTab)
watch(isMobile, redirectIfBlockedTab)

const onLogoChange = (e) => {
  const file = e.target.files[0]
  if (!file) return
  form.app_setting.business_logo = file
  logoPreview.value = URL.createObjectURL(file)
}

const previewMoney = computed(() => {
  try {
    return new Intl.NumberFormat(form.app_setting.locale, {
      style: 'currency', currency: form.app_setting.currency_code
    }).format(1234.56)
  } catch { return '—' }
})

const previewDate = computed(() => {
  try {
    return new Date().toLocaleDateString(form.app_setting.locale, {
      day: 'numeric', month: 'long', year: 'numeric'
    })
  } catch { return '—' }
})

const coerceClause = (c) => ({
  num:  Number(c.num) || 1,
  text: String(c.text || ''),
  bold: c.bold === true || c.bold === 'true',
})

const submit = () =>
  form
    .transform((data) => ({
      ...data,
      app_setting: {
        ...data.app_setting,
        two_wheeler_clauses:  (data.app_setting.two_wheeler_clauses  || []).map(coerceClause),
        four_wheeler_clauses: (data.app_setting.four_wheeler_clauses || []).map(coerceClause),
      }
    }))
    .patch(`/settings?tab=${activeTab.value}`, { preserveScroll: true, forceFormData: true })
</script>

<template>
  <PageShell :title="t('settings.title')">
    <SettingsTabs />
    <div class="max-w-xl w-full space-y-6">
      <div
        v-if="errorMessages.length"
        class="p-3 rounded-[var(--radius-md)] text-sm"
        :style="{ background: 'var(--dangerSoft)', border: '1px solid color-mix(in oklab, var(--danger) 35%, transparent)', color: 'var(--danger)' }"
        role="alert"
      >
        <ul class="list-disc list-inside space-y-1">
          <li
            v-for="(e, i) in errorMessages"
            :key="i"
          >
            {{ e }}
          </li>
        </ul>
      </div>

      <form
        v-show="activeTab !== 'appearance'"
        class="space-y-6"
        @submit.prevent="submit"
      >
        <!-- Localization tab -->
        <div
          v-show="activeTab === 'localization'"
          class="space-y-6"
        >
          <!-- Language -->
          <div
            class="rounded-xl p-5 space-y-3"
            :style="{ background: 'var(--surface)', border: '1px solid var(--line)' }"
          >
            <label class="block text-sm font-semibold text-[color:var(--inkSoft)]">{{ t('settings.language') }}</label>
            <div
              class="flex gap-2"
              role="group"
              :aria-label="t('settings.language')"
            >
              <button
                v-for="lang in [{ code: 'en', label: 'English' }, { code: 'th', label: 'ภาษาไทย' }, { code: 'ru', label: 'Русский' }]"
                :key="lang.code"
                type="button"
                class="flex-1 px-4 py-2.5 text-sm font-medium rounded-[var(--radius-md)] border transition-colors"
                :style="form.app_setting.language === lang.code
                  ? { backgroundColor: 'var(--accent)', color: 'var(--accentInk)', borderColor: 'var(--accent)' }
                  : { background: 'var(--surface)', color: 'var(--ink)', borderColor: 'var(--line)' }"
                :aria-pressed="form.app_setting.language === lang.code"
                @click="form.app_setting.language = lang.code"
              >
                {{ lang.label }}
              </button>
            </div>
          </div>

          <!-- Currency (canonical — locked) -->
          <div
            class="rounded-xl p-5 space-y-3"
            :style="{ background: 'var(--surface)', border: '1px solid var(--line)' }"
          >
            <label class="block text-sm font-semibold text-[color:var(--inkSoft)]">
              {{ t('settings.currency') }}
              <span class="ml-2 text-xs font-normal text-[color:var(--muted)]">
                · {{ t('settings.currency_locked') }}
              </span>
            </label>
            <div
              class="w-full px-3 py-2.5 rounded-[var(--radius-md)] border border-[color:var(--line)] bg-[color:var(--surfaceAlt)] text-[color:var(--inkSoft)] text-sm"
            >
              {{ setting.currency_code }}
            </div>
            <p class="text-xs text-[color:var(--muted)]">
              {{ t('settings.currency_lock_hint') }}
            </p>
          </div>

          <!-- Display currencies (public catalog) -->
          <div
            class="rounded-xl p-5 space-y-3"
            :style="{ background: 'var(--surface)', border: '1px solid var(--line)' }"
          >
            <label class="block text-sm font-semibold text-[color:var(--inkSoft)]">
              {{ t('settings.display_currencies') }}
            </label>
            <p class="text-xs text-[color:var(--muted)]">
              {{ t('settings.display_currencies_hint') }}
            </p>
            <div class="flex flex-wrap gap-2">
              <button
                v-for="code in display_currency_options"
                :key="code"
                type="button"
                :class="[
                  'px-3 py-1.5 rounded-full text-sm border transition',
                  form.app_setting.display_currencies.includes(code)
                    ? 'border-[color:var(--accent)] bg-[color:var(--accent)] text-[color:var(--accentInk)]'
                    : 'border-[color:var(--line)] text-[color:var(--inkSoft)]',
                  code === setting.currency_code ? 'cursor-not-allowed opacity-80' : 'cursor-pointer'
                ]"
                :disabled="code === setting.currency_code"
                @click="toggleDisplayCurrency(code)"
              >
                {{ code }}{{ code === setting.currency_code ? ' · ' + t('settings.canonical_tag') : '' }}
              </button>
            </div>
          </div>

          <!-- FX margin + live rates -->
          <div
            class="rounded-xl p-5 space-y-3"
            :style="{ background: 'var(--surface)', border: '1px solid var(--line)' }"
          >
            <label
              for="fx_margin"
              class="block text-sm font-semibold text-[color:var(--inkSoft)]"
            >{{ t('settings.fx_margin') }}</label>
            <p class="text-xs text-[color:var(--muted)]">
              {{ t('settings.fx_margin_hint') }}
            </p>
            <div class="flex items-center gap-2">
              <input
                id="fx_margin"
                v-model.number="form.app_setting.fx_margin_pct"
                type="number"
                min="0"
                max="50"
                step="0.1"
                class="w-24 px-3 py-2 rounded-[var(--radius-md)] border border-[color:var(--line)] bg-[color:var(--surface)] text-[color:var(--ink)] text-sm focus:outline-2 focus:outline-[color:var(--accent)]"
              />
              <span class="text-sm text-[color:var(--inkSoft)]">%</span>
            </div>

            <div class="pt-3 border-t border-[color:var(--line)]">
              <div class="flex items-center justify-between mb-2">
                <span class="text-sm font-semibold text-[color:var(--inkSoft)]">{{ t('settings.fx_rates') }}</span>
                <button
                  type="button"
                  class="text-xs px-2.5 py-1 rounded-full border border-[color:var(--line)] hover:border-[color:var(--accent)] text-[color:var(--inkSoft)]"
                  :disabled="fxRefreshing"
                  @click="refreshFxRates"
                >
                  {{ fxRefreshing ? t('settings.fx_refreshing') : t('settings.fx_refresh_now') }}
                </button>
              </div>
              <div v-if="fxRates.length === 0" class="text-xs text-[color:var(--muted)] italic">
                {{ t('settings.fx_no_rates') }}
              </div>
              <ul v-else class="space-y-1">
                <li
                  v-for="r in fxRates"
                  :key="r.quote"
                  class="flex items-center justify-between text-xs text-[color:var(--inkSoft)]"
                >
                  <span class="font-mono">1 {{ setting.currency_code }} = {{ r.rate.toFixed(6) }} {{ r.quote }}</span>
                  <span class="text-[color:var(--muted)]">
                    {{ formatDateTime(r.fetched_at) }}
                  </span>
                </li>
              </ul>
              <p v-if="fxFlash" class="text-xs mt-2" :class="fxFlashOk ? 'text-[color:var(--ok)]' : 'text-[color:var(--danger)]'">{{ fxFlash }}</p>
            </div>
          </div>

          <!-- Locale / Region -->
          <div
            class="rounded-xl p-5 space-y-3"
            :style="{ background: 'var(--surface)', border: '1px solid var(--line)' }"
          >
            <label
              for="locale"
              class="block text-sm font-semibold text-[color:var(--inkSoft)]"
            >{{ t('settings.locale') }}</label>
            <select
              id="locale"
              v-model="form.app_setting.locale"
              class="w-full px-3 py-2.5 rounded-[var(--radius-md)] border border-[color:var(--line)] bg-[color:var(--surface)] text-[color:var(--ink)] text-sm focus:outline-2 focus:outline-[color:var(--accent)]"
            >
              <option
                v-for="l in locales"
                :key="l.code"
                :value="l.code"
              >
                {{ l.label }}
              </option>
            </select>
          </div>

          <!-- Live preview -->
          <div
            class="rounded-xl p-5 space-y-2"
            :style="{ background: 'var(--accentSoft)', border: '1px solid color-mix(in oklab, var(--accent) 25%, transparent)' }"
          >
            <p
              class="text-xs font-semibold uppercase tracking-wider mb-3"
              :style="{ color: 'var(--accent)' }"
            >
              {{ t('settings.preview') }}
            </p>
            <div class="flex justify-between text-sm">
              <span class="text-[color:var(--inkSoft)]">{{ t('settings.currency') }}</span>
              <span class="fw-value tabular-nums text-[color:var(--ink)]">{{ previewMoney }}</span>
            </div>
            <div class="flex justify-between text-sm">
              <span class="text-[color:var(--inkSoft)]">{{ t('settings.date') }}</span>
              <span class="font-medium text-[color:var(--ink)]">{{ previewDate }}</span>
            </div>
            <div class="flex justify-between text-sm">
              <span class="text-[color:var(--inkSoft)]">{{ t('settings.language') }}</span>
              <span class="font-medium text-[color:var(--ink)] capitalize">{{ form.app_setting.language }}</span>
            </div>
          </div>
        </div>

        <!-- Agreement tab -->
        <div
          v-show="activeTab === 'agreement'"
          class="space-y-6"
        >
          <div
            class="rounded-xl p-5 space-y-4"
            :style="{ background: 'var(--surface)', border: '1px solid var(--line)' }"
          >
            <h2 class="text-sm font-semibold text-[color:var(--inkSoft)]">
              {{ t('settings.tabs.agreement') }}
            </h2>

            <div>
              <label class="block text-xs font-medium text-[color:var(--inkSoft)] mb-1">Business name</label>
              <input
                v-model="form.app_setting.business_name"
                type="text"
                :placeholder="t('settings.branding.businessNamePlaceholder')"
                class="w-full px-3 py-2 rounded-[var(--radius-md)] border border-[color:var(--line)] bg-[color:var(--surface)] text-[color:var(--ink)] text-sm focus:outline-2 focus:outline-[color:var(--accent)]"
              >
            </div>

            <div>
              <label class="block text-xs font-medium text-[color:var(--inkSoft)] mb-1">Agreement subtitle</label>
              <input
                v-model="form.app_setting.agreement_subtitle"
                type="text"
                :placeholder="t('settings.branding.agreementSubtitlePlaceholder')"
                class="w-full px-3 py-2 rounded-[var(--radius-md)] border border-[color:var(--line)] bg-[color:var(--surface)] text-[color:var(--ink)] text-sm focus:outline-2 focus:outline-[color:var(--accent)]"
              >
            </div>

            <div>
              <label class="block text-xs font-medium text-[color:var(--inkSoft)] mb-1">Footer text</label>
              <input
                v-model="form.app_setting.business_footer"
                type="text"
                :placeholder="t('settings.branding.businessFooterPlaceholder')"
                class="w-full px-3 py-2 rounded-[var(--radius-md)] border border-[color:var(--line)] bg-[color:var(--surface)] text-[color:var(--ink)] text-sm focus:outline-2 focus:outline-[color:var(--accent)]"
              >
            </div>

            <div>
              <label class="block text-sm font-medium text-[color:var(--inkSoft)] mb-1">
                {{ t('settings.branding.supportEmail') }}
              </label>
              <UiInput
                v-model="form.app_setting.support_email"
                type="email"
                :placeholder="t('settings.branding.supportEmailPlaceholder')"
              />
              <p class="text-xs text-[color:var(--muted)] mt-1">
                {{ t('settings.branding.supportEmailHint') }}
              </p>
            </div>

            <div>
              <label class="block text-sm font-medium text-[color:var(--ink)] mb-1">
                {{ t('settings.branding.driverFeeDefault.label') }}
              </label>
              <UiInput
                v-model.number="form.app_setting.driver_fee_cents"
                type="number"
                min="0"
                step="1"
                :placeholder="t('settings.branding.driverFeeDefault.placeholder')"
              />
              <p class="text-xs text-[color:var(--muted)] mt-1">
                {{ t('settings.branding.driverFeeDefault.help') }}
              </p>
            </div>

            <!-- Public channels live on their own constructor page now.
                 Settings → Branding keeps a pointer to it. -->
            <div class="rounded-[var(--radius-md)] border border-[color:var(--line)] p-4 flex items-center justify-between gap-3">
              <div>
                <h3 class="text-sm font-semibold text-[color:var(--ink)]">
                  {{ t('settings.branding.publicChannels.title') }}
                </h3>
                <p class="text-xs text-[color:var(--muted)] mt-1">
                  {{ t('settings.branding.publicChannels.hint') }}
                </p>
              </div>
              <Link
                href="/admin/public_channels"
                class="px-3 py-1.5 rounded-[var(--radius-button,8px)] border border-[color:var(--line)] text-sm font-medium text-[color:var(--ink)] hover:border-[color:var(--accent)] hover:text-[color:var(--accent)]"
              >
                {{ t('settings.branding.publicChannels.manage') }} →
              </Link>
            </div>

            <div>
              <label class="block text-xs font-medium text-[color:var(--inkSoft)] mb-1">Logo</label>
              <img
                v-if="logoPreview"
                :src="logoPreview"
                class="w-16 h-16 object-contain rounded mb-2"
                :style="{ border: '1px solid var(--line)' }"
                alt="Logo preview"
              >
              <input
                ref="logoFile"
                type="file"
                accept="image/*"
                class="w-full text-sm text-[color:var(--inkSoft)] file:mr-3 file:py-1.5 file:px-3 file:rounded-[var(--radius-md)] file:border-0 file:text-sm file:font-medium file:bg-[color:var(--accentSoft)] file:text-[color:var(--accent)] hover:file:brightness-95 file:cursor-pointer"
                @change="onLogoChange"
              >
            </div>

            <div>
              <div class="flex items-center justify-between mb-2">
                <label class="block text-xs font-medium text-[color:var(--inkSoft)]">Default agent signature</label>
                <div class="flex items-center gap-2">
                  <UiButton
                    type="button"
                    variant="secondary"
                    size="sm"
                    :disabled="signatureSaving"
                    @click="signatureModalOpen = true"
                  >
                    {{ signaturePreview ? 'Re-draw' : 'Draw signature' }}
                  </UiButton>
                  <UiButton
                    v-if="signaturePreview"
                    type="button"
                    variant="ghost"
                    size="sm"
                    :disabled="signatureSaving"
                    @click="removeSignature"
                  >
                    Remove
                  </UiButton>
                  <span
                    v-if="signatureSaving"
                    class="text-xs text-[color:var(--muted)]"
                  >Saving…</span>
                </div>
              </div>
              <p class="text-xs text-[color:var(--muted)] mb-2">
                Printed on the Lessor / Agent line of every contract PDF, in addition to the customer's signature.
              </p>
              <div
                v-if="signaturePreview"
                class="rounded-[var(--radius-md)] border border-dashed border-[color:var(--line)] bg-[color:var(--surfaceAlt)] p-3 inline-block"
              >
                <img
                  :src="signaturePreview"
                  alt="Default signature preview"
                  class="h-16 object-contain"
                  style="background: #fff;"
                >
              </div>
              <p
                v-else
                class="text-xs text-[color:var(--muted)]"
              >
                Not set
              </p>
            </div>

            <!-- Agreement Clauses -->
            <div class="space-y-4 pt-6 border-t border-[color:var(--line)]">
              <div>
                <h3 class="text-sm font-semibold text-[color:var(--ink)] mb-1">
                  {{ t('settings.clauses.title') }}
                </h3>
                <p class="text-xs text-[color:var(--muted)]">
                  {{ t('settings.clauses.hint') }}
                </p>
              </div>

              <UiSegmented
                v-model="clauseClass"
                :options="[
                  { value: 'two_wheeler', label: t('vehicles.classes.two_wheeler') },
                  { value: 'four_wheeler', label: t('vehicles.classes.four_wheeler') }
                ]"
              />

              <div class="space-y-3">
                <div
                  v-for="(c, idx) in activeClauses"
                  :key="idx"
                  class="rounded-[var(--radius-md)] border border-[color:var(--line)] p-3 space-y-2"
                >
                  <div class="flex items-center gap-2">
                    <UiInput
                      v-model.number="c.num"
                      type="number"
                      min="1"
                      max="99"
                      class="w-20"
                      :aria-label="t('settings.clauses.number')"
                    />
                    <label class="text-xs flex items-center gap-1 text-[color:var(--inkSoft)]">
                      <input
                        v-model="c.bold"
                        type="checkbox"
                      > {{ t('settings.clauses.bold') }}
                    </label>
                    <div class="ml-auto flex gap-1">
                      <UiButton
                        type="button"
                        variant="ghost"
                        size="sm"
                        :disabled="idx === 0"
                        @click="moveClause(idx, -1)"
                      >
                        ↑
                      </UiButton>
                      <UiButton
                        type="button"
                        variant="ghost"
                        size="sm"
                        :disabled="idx === activeClauses.length - 1"
                        @click="moveClause(idx, 1)"
                      >
                        ↓
                      </UiButton>
                      <UiButton
                        type="button"
                        variant="ghost"
                        size="sm"
                        @click="removeClause(idx)"
                      >
                        {{ t('common.delete') }}
                      </UiButton>
                    </div>
                  </div>
                  <UiTextarea
                    v-model="c.text"
                    :rows="3"
                    :placeholder="t('settings.clauses.placeholder')"
                  />
                </div>

                <UiButton
                  type="button"
                  variant="secondary"
                  size="sm"
                  @click="addClause"
                >
                  + {{ t('settings.clauses.add') }}
                </UiButton>
              </div>
            </div>
          </div>
        </div>

        <StickyActionBar>
          <UiButton
            type="submit"
            variant="primary"
            :loading="form.processing"
          >
            {{ form.processing ? t('common.loading') : t('settings.save') }}
          </UiButton>
        </StickyActionBar>
      </form>

      <!-- Appearance tab -->
      <div
        v-show="activeTab === 'appearance'"
        class="rounded-xl p-5 space-y-4"
        :style="{ background: 'var(--surface)', border: '1px solid var(--line)' }"
      >
        <h2 class="text-sm font-semibold text-[color:var(--inkSoft)]">
          {{ t('settings.tabs.appearance') }}
        </h2>
        <TweakControls />
      </div>
    </div>

    <SignaturePadModal
      :open="signatureModalOpen"
      title="Default agent signature"
      @close="signatureModalOpen = false"
      @save="onSignatureSave"
    />
  </PageShell>
</template>
